/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Optional, ViewEncapsulation } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { trigger, state, style, animate, transition, keyframes, } from '@angular/animations';
import { CdkColumnDef } from '@angular/cdk/table';
import { merge } from 'rxjs/observable/merge';
import { MatSort } from './sort';
import { MatSortHeaderIntl } from './sort-header-intl';
import { getSortHeaderNotContainedWithinSortError } from './sort-errors';
import { AnimationCurves, AnimationDurations } from '@angular/material/core';
const /** @type {?} */ SORT_ANIMATION_TRANSITION = AnimationDurations.ENTERING + ' ' + AnimationCurves.STANDARD_CURVE;
/**
 * Applies sorting behavior (click to change sort) and styles to an element, including an
 * arrow to display the current sort direction.
 *
 * Must be provided with an id and contained within a parent MatSort directive.
 *
 * If used on header cells in a CdkTable, it will automatically default its id from its containing
 * column definition.
 */
export class MatSortHeader {
    /**
     * @param {?} _intl
     * @param {?} changeDetectorRef
     * @param {?} _sort
     * @param {?} _cdkColumnDef
     */
    constructor(_intl, changeDetectorRef, _sort, _cdkColumnDef) {
        this._intl = _intl;
        this._sort = _sort;
        this._cdkColumnDef = _cdkColumnDef;
        /**
         * Sets the position of the arrow that displays when sorted.
         */
        this.arrowPosition = 'after';
        if (!_sort) {
            throw getSortHeaderNotContainedWithinSortError();
        }
        this._rerenderSubscription = merge(_sort.sortChange, _intl.changes).subscribe(() => {
            changeDetectorRef.markForCheck();
        });
    }
    /**
     * Overrides the disable clear value of the containing MatSort for this MatSortable.
     * @return {?}
     */
    get disableClear() { return this._disableClear; }
    /**
     * @param {?} v
     * @return {?}
     */
    set disableClear(v) { this._disableClear = coerceBooleanProperty(v); }
    /**
     * @return {?}
     */
    ngOnInit() {
        if (!this.id && this._cdkColumnDef) {
            this.id = this._cdkColumnDef.name;
        }
        this._sort.register(this);
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this._sort.deregister(this);
        this._rerenderSubscription.unsubscribe();
    }
    /**
     * Whether this MatSortHeader is currently sorted in either ascending or descending order.
     * @return {?}
     */
    _isSorted() {
        return this._sort.active == this.id &&
            (this._sort.direction === 'asc' || this._sort.direction === 'desc');
    }
}
MatSortHeader.decorators = [
    { type: Component, args: [{selector: '[mat-sort-header]',
                template: "<div class=\"mat-sort-header-container\" [class.mat-sort-header-position-before]=\"arrowPosition == 'before'\"><button class=\"mat-sort-header-button\" type=\"button\" [attr.aria-label]=\"_intl.sortButtonLabel(id)\"><ng-content></ng-content></button><div *ngIf=\"_isSorted()\" class=\"mat-sort-header-arrow\" [@indicatorToggle]=\"_sort.direction\"><div class=\"mat-sort-header-stem\"></div><div class=\"mat-sort-header-indicator\" [@indicator]=\"_sort.direction\"><div class=\"mat-sort-header-pointer-left\" [@leftPointer]=\"_sort.direction\"></div><div class=\"mat-sort-header-pointer-right\" [@rightPointer]=\"_sort.direction\"></div><div class=\"mat-sort-header-pointer-middle\"></div></div></div></div><span class=\"cdk-visually-hidden\" *ngIf=\"_isSorted()\">{{_intl.sortDescriptionLabel(id, _sort.direction)}}</span>",
                styles: [".mat-sort-header-container{display:flex;cursor:pointer}.mat-sort-header-position-before{flex-direction:row-reverse}.mat-sort-header-button{border:none;background:0 0;display:flex;align-items:center;padding:0;cursor:pointer;outline:0;font:inherit;color:currentColor}.mat-sort-header-arrow{height:12px;width:12px;margin:0 0 0 6px;position:relative;display:flex}.mat-sort-header-position-before .mat-sort-header-arrow{margin:0 6px 0 0}.mat-sort-header-stem{background:currentColor;height:10px;width:2px;margin:auto;display:flex;align-items:center}.mat-sort-header-indicator{width:100%;height:2px;display:flex;align-items:center;position:absolute;top:0;left:0;transition:225ms cubic-bezier(.4,0,.2,1)}.mat-sort-header-pointer-middle{margin:auto;height:2px;width:2px;background:currentColor;transform:rotate(45deg)}.mat-sort-header-pointer-left,.mat-sort-header-pointer-right{background:currentColor;width:6px;height:2px;transition:225ms cubic-bezier(.4,0,.2,1);position:absolute;top:0}.mat-sort-header-pointer-left{transform-origin:right;left:0}.mat-sort-header-pointer-right{transform-origin:left;right:0}"],
                host: {
                    '(click)': '_sort.sort(this)',
                    '[class.mat-sort-header-sorted]': '_isSorted()',
                },
                encapsulation: ViewEncapsulation.None,
                preserveWhitespaces: false,
                changeDetection: ChangeDetectionStrategy.OnPush,
                animations: [
                    trigger('indicator', [
                        state('asc', style({ transform: 'translateY(0px)' })),
                        // 10px is the height of the sort indicator, minus the width of the pointers
                        state('desc', style({ transform: 'translateY(10px)' })),
                        transition('asc <=> desc', animate(SORT_ANIMATION_TRANSITION))
                    ]),
                    trigger('leftPointer', [
                        state('asc', style({ transform: 'rotate(-45deg)' })),
                        state('desc', style({ transform: 'rotate(45deg)' })),
                        transition('asc <=> desc', animate(SORT_ANIMATION_TRANSITION))
                    ]),
                    trigger('rightPointer', [
                        state('asc', style({ transform: 'rotate(45deg)' })),
                        state('desc', style({ transform: 'rotate(-45deg)' })),
                        transition('asc <=> desc', animate(SORT_ANIMATION_TRANSITION))
                    ]),
                    trigger('indicatorToggle', [
                        transition('void => asc', animate(SORT_ANIMATION_TRANSITION, keyframes([
                            style({ transform: 'translateY(25%)', opacity: 0 }),
                            style({ transform: 'none', opacity: 1 })
                        ]))),
                        transition('asc => void', animate(SORT_ANIMATION_TRANSITION, keyframes([
                            style({ transform: 'none', opacity: 1 }),
                            style({ transform: 'translateY(-25%)', opacity: 0 })
                        ]))),
                        transition('void => desc', animate(SORT_ANIMATION_TRANSITION, keyframes([
                            style({ transform: 'translateY(-25%)', opacity: 0 }),
                            style({ transform: 'none', opacity: 1 })
                        ]))),
                        transition('desc => void', animate(SORT_ANIMATION_TRANSITION, keyframes([
                            style({ transform: 'none', opacity: 1 }),
                            style({ transform: 'translateY(25%)', opacity: 0 })
                        ]))),
                    ])
                ]
            },] },
];
/**
 * @nocollapse
 */
MatSortHeader.ctorParameters = () => [
    { type: MatSortHeaderIntl, },
    { type: ChangeDetectorRef, },
    { type: MatSort, decorators: [{ type: Optional },] },
    { type: CdkColumnDef, decorators: [{ type: Optional },] },
];
MatSortHeader.propDecorators = {
    'id': [{ type: Input, args: ['mat-sort-header',] },],
    'arrowPosition': [{ type: Input },],
    'start': [{ type: Input, args: ['start',] },],
    'disableClear': [{ type: Input },],
};
function MatSortHeader_tsickle_Closure_declarations() {
    /** @type {?} */
    MatSortHeader.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatSortHeader.ctorParameters;
    /** @type {?} */
    MatSortHeader.propDecorators;
    /** @type {?} */
    MatSortHeader.prototype._rerenderSubscription;
    /**
     * ID of this sort header. If used within the context of a CdkColumnDef, this will default to
     * the column's name.
     * @type {?}
     */
    MatSortHeader.prototype.id;
    /**
     * Sets the position of the arrow that displays when sorted.
     * @type {?}
     */
    MatSortHeader.prototype.arrowPosition;
    /**
     * Overrides the sort start value of the containing MatSort for this MatSortable.
     * @type {?}
     */
    MatSortHeader.prototype.start;
    /** @type {?} */
    MatSortHeader.prototype._disableClear;
    /** @type {?} */
    MatSortHeader.prototype._intl;
    /** @type {?} */
    MatSortHeader.prototype._sort;
    /** @type {?} */
    MatSortHeader.prototype._cdkColumnDef;
}
//# sourceMappingURL=sort-header.js.map